import { EXPORT_SEARCH_URL_PARAM } from '@aibees/elephant/context/search-params-url-sync';
import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import { resolveExportParams } from '@aibees/elephant/feature/exports-management-table/utils';
import { SearchModality } from '@aibees/elephant/feature/search-table-company/hooks/types';
import { useBrowseSearchExports } from '@aibees/elephant/hooks/use-browse-search-exports';
import { useSearchHistoryStore } from '@aibees/elephant/store/search-history-store';
import {
  useSearchParamsStore,
  type SearchTableScope,
} from '@aibees/elephant/store/search-params-store';
import { SearchType } from '@aibees/elephant/types';
import type { SearchBrowseParams } from '@aibees/elephant/types';
import { useNotify } from '@aibees/shared/hooks';
import { objSanitizeNilValues } from '@aibees/shared/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Some params objects historically carried the scope `SearchType`
 * ('Company' | 'People') as a key. It is not an applied filter, so strip it
 * before hydrating the store (mirrors `useApplySavedSearch`).
 *
 * "View Search" must always land on the TOTAL records tab regardless of which
 * modality was active when the export was created, so the stored modality is
 * overridden here. We deliberately do NOT touch `metadata.params` (the export
 * keeps its original modality) so the product team can revert to honoring the
 * stored modality later without a backfill.
 */
function sanitizeExportSearchValue(
  value: Partial<SearchBrowseParams>
): Partial<SearchBrowseParams> {
  const next = { ...value } as Record<string, unknown>;
  if (next.type === SearchType.Company || next.type === SearchType.People) {
    delete next.type;
  }
  next.modality = SearchModality.TOTAL;
  return next as Partial<SearchBrowseParams>;
}

/**
 * Reads the one-shot `?export-search=<exportId>` link param, fetches that export,
 * replaces the current scope's params with the ORIGINAL search that produced it
 * (`metadata.params`, falling back to `metadata.url`), then strips the param.
 *
 * This is the exports counterpart of `useApplySavedSearch`: "View Search" links
 * carry only the short export id (never the full filter set), so a new tab can
 * restore the original search without ever putting a long filter query string in
 * the URL (which is what could hit the web server's URL length limit / 414).
 *
 * The id is LATCHED from the initial URL because `export-search` is excluded from
 * the filter sync (`omitNonFilterParams`); the sync would otherwise strip it
 * before the async fetch resolves.
 */
export function useApplyExportSearch() {
  const scope = useSearchTableScope();
  const [, setSearchParams] = useSearchParams();
  const replaceParamsForScope = useSearchParamsStore.useReplaceParamsForScope();
  const resetHistoryTo = useSearchHistoryStore.useResetTo();
  const notify = useNotify();

  const [exportId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get(EXPORT_SEARCH_URL_PARAM)
  );
  const appliedRef = useRef(false);

  const hasScope =
    scope === 'company' || scope === 'people' || scope === 'employees';

  const { data, isError, isSuccess } = useBrowseSearchExports({
    id: exportId ?? undefined,
    page: 0,
    size: 1,
  });

  const stripExportId = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(EXPORT_SEARCH_URL_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (!exportId || !hasScope || appliedRef.current) return;

    if (isError) {
      appliedRef.current = true;
      notify.error('This export search could not be loaded.');
      stripExportId();
      return;
    }

    if (isSuccess) {
      const applied = resolveExportParams(data?.content?.[0]);

      // No export / no stored search: don't wipe the scope's current filters.
      if (!applied) {
        appliedRef.current = true;
        notify.error('This export search could not be loaded.');
        stripExportId();
        return;
      }

      appliedRef.current = true;
      const sanitized = sanitizeExportSearchValue(applied);
      replaceParamsForScope(scope as SearchTableScope, sanitized);
      // Seed the applied state as the undo/redo baseline so the header "undo"
      // returns to the original search (and can't drop below it into empty filters).
      resetHistoryTo(
        scope as SearchTableScope,
        objSanitizeNilValues(sanitized) as Record<string, string>
      );
      stripExportId();
    }
  }, [
    exportId,
    hasScope,
    scope,
    data,
    isError,
    isSuccess,
    replaceParamsForScope,
    resetHistoryTo,
    notify,
    stripExportId,
  ]);
}
